// Newsletter editor schema.
//
// Phase 1 scope: doc, newsletterHeading, newsletterParagraph + bold/italic/link marks.
// Phase 2 scope: textColor, highlight, fontSize marks + textAlign attr on heading/paragraph.
// All top-level blocks live in the `newsletterBlock` group and carry a stable `id` attr.

import { Node, Extension, mergeAttributes } from "@tiptap/core"
import { Document } from "@tiptap/extension-document"
import { TextStyle } from "@tiptap/extension-text-style"
import { Color } from "@tiptap/extension-color"
import { Highlight } from "@tiptap/extension-highlight"
import { TextAlign } from "@tiptap/extension-text-align"
import Focus from "@tiptap/extension-focus"
import { Placeholder } from "@tiptap/extension-placeholder"
import { Plugin, PluginKey } from "@tiptap/pm/state"

import { FontSize } from "./font_size"
import { FontFamily } from "./font_family"
import { NewsletterDivider } from "./divider"
import { insertBlockAfter } from "./commands"
import { NewsletterImage, NewsletterImageMedia, ImageCaption } from "./image"
import { NewsletterImageRow, NewsletterImageRowItem } from "./image_row"
import { NewsletterImageText } from "./image_text"
import { NewsletterTextBox } from "./text_box"
import { BlockChrome } from "./block_chrome"
import { StructuralDeleteGuard } from "./structural_delete_guard"
import { generateId, idAttributeSpec, nodeInGroup } from "./attributes"

// Top-level container. Only newsletterBlock children allowed.
export const NewsletterDocument = Document.extend({
  content: "newsletterBlock+",
})

// Shared attribute spec for top-level blocks.
const idAttribute = { id: idAttributeSpec }

export const NewsletterParagraph = Node.create({
  name: "newsletterParagraph",
  group: "newsletterBlock newsletterText",
  content: "inline*",
  priority: 1000,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addAttributes() {
    return { ...idAttribute }
  },

  parseHTML() {
    return [{ tag: "p" }]
  },

  renderHTML({ HTMLAttributes }) {
    return ["p", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addCommands() {
    return {
      setNewsletterParagraph:
        () =>
        ({ commands }) =>
          commands.setNode(this.name),
      insertNewsletterParagraphAfter:
        () =>
        (props) =>
          insertBlockAfter("newsletterParagraph")(props),
    }
  },
})

const HEADING_LEVELS = [1, 2, 3]

export const NewsletterHeading = Node.create({
  name: "newsletterHeading",
  group: "newsletterBlock newsletterText",
  content: "inline*",
  defining: true,

  addOptions() {
    return { levels: HEADING_LEVELS, HTMLAttributes: {} }
  },

  addAttributes() {
    return {
      ...idAttribute,
      level: {
        default: 1,
        rendered: false,
        parseHTML: (el) => Number(el.tagName.replace("H", "")) || 1,
      },
    }
  },

  parseHTML() {
    return this.options.levels.map((level) => ({ tag: `h${level}`, attrs: { level } }))
  },

  renderHTML({ node, HTMLAttributes }) {
    const level = this.options.levels.includes(node.attrs.level)
      ? node.attrs.level
      : this.options.levels[0]
    return [`h${level}`, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addCommands() {
    return {
      setNewsletterHeading:
        (attrs) =>
        ({ commands }) =>
          commands.setNode(this.name, attrs),
      toggleNewsletterHeading:
        (attrs) =>
        ({ commands }) =>
          commands.toggleNode(this.name, "newsletterParagraph", attrs),
      insertNewsletterHeadingAfter:
        (level = 2) =>
        (props) =>
          insertBlockAfter("newsletterHeading", { level })(props),
    }
  },
})

// Ensures every top-level newsletterBlock has a unique stable id.
// Runs as an appendTransaction so it folds into the same tick that introduced
// the new node (paste, insert command, undo, etc.) — no flicker or separate dispatch.
//
// Group membership is whitespace-separated in the schema spec, e.g.
// `newsletterParagraph` is in `"newsletterBlock newsletterText"`. String
// equality misses every multi-group node; `nodeInGroup` splits and matches.
export const EnsureBlockIds = Extension.create({
  name: "ensureBlockIds",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("ensureBlockIds"),
        appendTransaction: (_transactions, _oldState, newState) => {
          let tr = null
          const seen = new Set()
          newState.doc.forEach((node, offset) => {
            if (!nodeInGroup(node.type, "newsletterBlock")) return
            const id = node.attrs.id
            if (!id || seen.has(id)) {
              tr = tr || newState.tr
              const fresh = generateId()
              tr.setNodeMarkup(offset, undefined, { ...node.attrs, id: fresh })
              seen.add(fresh)
            } else {
              seen.add(id)
            }
          })
          return tr
        },
      }),
    ]
  },
})

export const newsletterExtensions = [
  NewsletterDocument,
  NewsletterParagraph,
  NewsletterHeading,
  NewsletterDivider,
  NewsletterImage,
  NewsletterImageMedia,
  ImageCaption,
  NewsletterImageRow,
  NewsletterImageRowItem,
  NewsletterImageText,
  NewsletterTextBox,
  BlockChrome,
  StructuralDeleteGuard,
  EnsureBlockIds,
  TextStyle,
  FontSize,
  FontFamily,
  Color.configure({ types: ["textStyle"] }),
  Highlight.configure({ multicolor: true }),
  TextAlign.configure({
    types: ["newsletterHeading", "newsletterParagraph", "imageCaption"],
    alignments: ["left", "center", "right"],
    defaultAlignment: "left",
  }),
  // Highlights the outermost block containing the caret. `shallowest` picks
  // the top-level newsletterBlock so the entire active block lights up rather
  // than just the paragraph inside an image+text.
  Focus.configure({
    className: "nl-has-focus",
    mode: "shallowest",
  }),
  Placeholder.configure({
    placeholder: ({ node }) => {
      switch (node.type.name) {
        case "newsletterHeading": {
          const level = node.attrs.level || 1
          return `Heading ${level}`
        }
        case "newsletterParagraph":
          return "Write your text here…"
        case "imageCaption":
          return "Write caption…"
        default:
          return ""
      }
    },
    showOnlyWhenEditable: true,
    showOnlyCurrent: false,
    includeChildren: true,
    emptyEditorClass: "nl-editor-empty",
    emptyNodeClass: "nl-node-empty",
  }),
]
